import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'
import { ArrowRight, ChevronDown, Languages } from 'lucide-react'
import { supportedLanguages, type SupportedLanguage } from '@/i18n'
import { translatePath } from '@/i18n/routes'
import { cn } from '@/lib/utils'

/**
 * Metadata for every language this selector knows how to render.
 * At runtime the dropdown is filtered down to the project's `supportedLanguages`,
 * so you can enable a new language simply by adding its code to
 * `src/i18n/index.ts` — as long as the metadata exists here the UI picks it up.
 */
interface LanguageMeta {
  code: string
  flag: string
  name: string
  short: string
}

const languagesMeta: LanguageMeta[] = [
  { code: 'it', flag: '🇮🇹', name: 'Italiano', short: 'IT' },
  { code: 'en', flag: '🇬🇧', name: 'English', short: 'EN' },
  { code: 'fr', flag: '🇫🇷', name: 'Français', short: 'FR' },
  { code: 'es', flag: '🇪🇸', name: 'Español', short: 'ES' },
  { code: 'de', flag: '🇩🇪', name: 'Deutsch', short: 'DE' },
  { code: 'pt', flag: '🇵🇹', name: 'Português', short: 'PT' },
  { code: 'pl', flag: '🇵🇱', name: 'Polski', short: 'PL' },
  { code: 'ja', flag: '🇯🇵', name: '日本語', short: 'JA' },
]

const languages = languagesMeta.filter(
  (l): l is LanguageMeta & { code: SupportedLanguage } =>
    (supportedLanguages as readonly string[]).includes(l.code)
)

/** Codice lingua su una paletta del tabellone partenze (vedi components/home/DepartureBoard). */
function CodeFlap({ code, isInverted = false }: { code: string; isInverted?: boolean }) {
  return (
    <span aria-hidden="true" className="flex gap-0.5 font-body text-xs font-semibold leading-none">
      {code.split('').map((char, i) => (
        <span key={i} className={cn('grid h-[1.35rem] w-[1.1rem] place-items-center rounded-[3px]', isInverted ? 'bg-paper text-ink' : 'bg-ink text-paper')}>
          {char}
        </span>
      ))}
    </span>
  )
}

interface LanguageSelectorProps {
  variant?: 'desktop' | 'mobile'
}

export function LanguageSelector({ variant = 'desktop' }: LanguageSelectorProps) {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const currentLang = (i18n.language || supportedLanguages[0]) as SupportedLanguage
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)

  const currentLanguage =
    languages.find((l) => l.code === currentLang) || languages[0]

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      setIsOpen(false)
      triggerRef.current?.focus()
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen])

  const switchLanguage = (newLang: SupportedLanguage) => {
    if (newLang === currentLang) {
      setIsOpen(false)
      return
    }

    const newPath = translatePath(location.pathname, newLang, location.search)

    i18n.changeLanguage(newLang)
    navigate(newPath)
    setIsOpen(false)
  }

  if (variant === 'mobile') {
    return (
      // Due colonne solo se ci stanno nome e codice interi (non nella sidebar dell'area riservata né a 320px).
      <div className="grid grid-cols-[repeat(auto-fit,minmax(8.5rem,1fr))] gap-3 py-3">
        {languages.map((lang) => {
          const isCurrent = lang.code === currentLang
          return (
            <button
              key={lang.code}
              type="button"
              onClick={() => switchLanguage(lang.code)}
              aria-current={isCurrent || undefined}
              className={cn(
                'flex items-center gap-3 rounded-lg border-2 border-ink px-3 py-2.5 text-left text-sm font-medium transition-[transform,box-shadow] duration-200 ease-out-expo active:translate-y-0.5',
                isCurrent ? 'bg-ink text-paper' : 'bg-paper text-ink shadow-[3px_3px_0_var(--color-ink)] active:shadow-none'
              )}
            >
              <CodeFlap code={lang.short} isInverted={isCurrent} />
              {lang.name}
            </button>
          )
        })}
      </div>
    )
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="group flex items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium text-ink transition-colors hover:bg-ink/5"
        aria-expanded={isOpen}
        aria-controls="language-menu"
        aria-label={`${t('language.label')}: ${currentLanguage.name}`}
      >
        <span className="text-base leading-none" aria-hidden="true">{currentLanguage.flag}</span>
        <CodeFlap code={currentLanguage.short} />
        <ChevronDown aria-hidden="true" className={cn('size-3.5 transition-transform duration-300 ease-out-expo', isOpen && 'rotate-180')} />
      </button>

      {isOpen && (
        <div
          id="language-menu"
          className={cn('absolute right-0 z-50 w-56 overflow-hidden', 'mt-3 origin-top-right', 'rounded-xl border-2 border-ink bg-paper shadow-[4px_4px_0_var(--color-ink)] motion-safe:animate-reveal')}
        >
          <p className="flex items-center justify-between bg-sea/15 px-4 pt-3 pb-2.5 font-body text-[0.6rem] font-semibold uppercase tracking-[0.2em] text-sea-ink">
            {t('language.label')}
            <Languages aria-hidden="true" className="size-3.5" />
          </p>
          <span aria-hidden="true" className="ticket-perforation block" />
          <ul className="p-1.5">
            {languages.map((lang) => {
              const isCurrent = lang.code === currentLang
              return (
                <li key={lang.code}>
                  <button
                    type="button"
                    onClick={() => switchLanguage(lang.code)}
                    aria-current={isCurrent || undefined}
                    className={cn(
                      'group/item flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left text-sm text-ink transition-colors hover:bg-paper-deep',
                      isCurrent && 'font-semibold'
                    )}
                  >
                    <span className="text-base leading-none" aria-hidden="true">{lang.flag}</span>
                    <span className="flex-1">{lang.name}</span>
                    {isCurrent ? (
                      <span aria-hidden="true" className="size-2 rounded-full bg-sun" />
                    ) : (
                      <ArrowRight aria-hidden="true" className="size-3.5 -translate-x-1 text-ink-soft opacity-0 transition-[transform,opacity] duration-200 ease-out-expo group-hover/item:translate-x-0 group-hover/item:opacity-100" />
                    )}
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )
}
